import { PaymentMethodEnum } from '@/types/common';
import { stripeInPageActionApmMethods, stripeInPageQrMethods } from '@/constants/stripe';

// What a Stripe finalize response (status: redirect) needs next. Kept as a
// pure, dependency-light classifier so the whole redirect-vs-action routing
// decision lives in one readable, unit-tested place. The four kinds map to four
// fully separate flows:
//   - ThreeDS       → card + wallet 3DS via <StripeCard3DSForm/>
//                     (`threeDSSecurityState` channel).
//   - ApmNextAction → generic in-page APMs (e.g. Bizum) via
//                     <StripeApmNextActionForm/> (`stripeApmAction` channel).
//   - InPageQr      → PIX / CashApp / UPI QR via <CashAppRedirect/>
//                     (onRedirect path).
//   - Redirect      → hosted `redirect_to_url` via <RedirectPayment/>
//                     (onRedirect path). Swish also lands here: it has no hosted
//                     URL, but `usePaymentResults` rewrites it to the top-level
//                     PAYMENT_HANDLER tab URL before classification.
export enum StripePostFinalizeAction {
  ThreeDS = 'threeds',
  ApmNextAction = 'apm_next_action',
  InPageQr = 'in_page_qr',
  Redirect = 'redirect',
}

/**
 * Classifies the follow-up a Stripe `status: redirect` response requires. See
 * {@link StripePostFinalizeAction} for how each kind is routed. Cards + wallets
 * resolve 3DS in-page (independent of any URL); generic in-page APMs (Bizum, …)
 * and an unknown APM that returned no hosted URL resolve via `handleNextAction`;
 * PIX/CashApp/UPI render a QR; everything with a hosted URL redirects (Swish is
 * given the PAYMENT_HANDLER-tab URL upstream, so it classifies as a redirect).
 */
export const getStripePostFinalizeAction = ({
  paymentMethod,
  redirectUrl,
}: {
  paymentMethod?: PaymentMethodEnum;
  redirectUrl?: string;
}): StripePostFinalizeAction => {
  const isCardOrWallet =
    paymentMethod === PaymentMethodEnum.CREDIT_CARD ||
    paymentMethod === PaymentMethodEnum.GOOGLE_PAY ||
    paymentMethod === PaymentMethodEnum.APPLE_PAY;
  const isInPageQr = !!paymentMethod && stripeInPageQrMethods.includes(paymentMethod);
  const isApmNextAction = !!paymentMethod && stripeInPageActionApmMethods.includes(paymentMethod);

  if (isCardOrWallet) {
    return StripePostFinalizeAction.ThreeDS;
  }
  if (isInPageQr) {
    return StripePostFinalizeAction.InPageQr;
  }
  if (isApmNextAction) {
    return StripePostFinalizeAction.ApmNextAction;
  }

  // Hosted-redirect APMs (Klarna, iDEAL, EPS, Bancontact, Amazon Pay, …) always
  // carry a `redirect_to_url`. An unknown APM with no URL falls back to the
  // in-page next-action driver, which is the safe (non-stuck) behavior.
  return redirectUrl ? StripePostFinalizeAction.Redirect : StripePostFinalizeAction.ApmNextAction;
};
